import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { PiUser } from 'react-icons/pi';
import { SEVERITY_RANK } from '../../contexts/FarmStatusContext';
import { statusColor } from '../../config/sensorRanges';
import { RADIUS } from './ui';
import { IST, alpha, plural, hhmm24 } from './helpers';

// The hero's cover: a chosen photo of the farm's own strawberry flowers (Rack A),
// not the newest upload, which is a working photo and often cluttered. Swap the
// files in public/hero to change it.
const COVER = {
  src: '/hero/strawberry-flowers-1800.jpg',
  srcSet: '/hero/strawberry-flowers-900.jpg 900w, /hero/strawberry-flowers-1800.jpg 1800w',
  alt: 'Strawberry flowers and green fruit on Rack A',
  caption: 'Murano in flower, Rack A',
};

/* The farm first: the newest real photo of the crop, with the day's verdict and
   four facts set on it. Without a photo it is a plain card with the same text. */
const HeroShell = styled.header`
  position: relative;
  min-height: 256px;
  border-radius: ${RADIUS}px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 22px 24px 20px;
  color: ${p => (p.$photo ? '#fff' : p.theme.colors.text)};
  background: ${p => (p.$photo ? '#16201B' : p.theme.colors.surface)};
  border: 1px solid ${p => (p.$photo ? 'transparent' : p.theme.colors.border)};

  > img { position: absolute; inset: 0; z-index: 0; width: 100%; height: 100%; object-fit: cover; object-position: 60% 40%; }
  /* Darken where the words sit, keep the crop visible on the right. Painted after
     the photo (same z-index, later in order), under the text. */
  &::after {
    content: '';
    position: absolute; inset: 0; z-index: 0;
    display: ${p => (p.$photo ? 'block' : 'none')};
    background:
      linear-gradient(90deg, rgba(12, 22, 17, 0.82) 0%, rgba(12, 22, 17, 0.55) 45%, rgba(12, 22, 17, 0.05) 100%),
      linear-gradient(0deg, rgba(12, 22, 17, 0.55) 0%, transparent 45%);
  }
  > *:not(img) { position: relative; z-index: 1; }
  > .cap { position: absolute; }

  .top { display: flex; align-items: center; flex-wrap: wrap; gap: 8px 12px; padding-right: 180px; }
  .date { font-size: 13px; opacity: ${p => (p.$photo ? 0.8 : 1)}; color: ${p => (p.$photo ? 'inherit' : p.theme.colors.textSecondary)}; }
  h1 {
    margin: 10px 0 0;
    font-size: 28px;
    line-height: 1.18;
    font-weight: 500;
    letter-spacing: -0.025em;
    max-width: 22ch;
  }
  .sub { margin-top: 8px; font-size: 14px; opacity: ${p => (p.$photo ? 0.82 : 1)}; color: ${p => (p.$photo ? 'inherit' : p.theme.colors.textSecondary)}; }
  .cap { position: absolute; right: 16px; top: 16px; font-size: 12px; opacity: 0.85; text-align: right; }

  @media (max-width: 760px) {
    min-height: 0;
    padding: 18px 16px 16px;
    h1 { font-size: 21px; max-width: none; }
    .cap { display: none; }
    .top { padding-right: 0; }
    &::after { background: linear-gradient(0deg, rgba(12, 22, 17, 0.88) 0%, rgba(12, 22, 17, 0.55) 60%, rgba(12, 22, 17, 0.3) 100%); }
  }
`;

const Mark = styled.span`
  display: inline-block;
  width: 10px; height: 10px;
  border-radius: 50%;
  margin-right: 10px;
  vertical-align: 4px;
  background: ${p => statusColor(p.theme, p.$status)};
  box-shadow: 0 0 0 4px ${p => alpha(statusColor(p.theme, p.$status), 0.28)};
`;

/* "Manager in since 09:12": shown only between today's check-in and check-out. Part of
   the date line, in its type and colour, so it reads as a fact about today and not as
   one more chip competing with the headline. */
const OnSite = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 0;
  font-size: 13px;
  text-decoration: none;
  white-space: nowrap;
  opacity: ${p => (p.$photo ? 0.8 : 1)};
  color: ${p => (p.$photo ? 'inherit' : p.theme.colors.textSecondary)};
  transition: opacity 0.15s ease, color 0.15s ease;

  svg { width: 16px; height: 16px; flex-shrink: 0; }
  b { font-weight: 500; }
  &:hover { opacity: 1; color: ${p => (p.$photo ? 'inherit' : p.theme.colors.text)}; text-decoration: underline; text-underline-offset: 3px; }
  &:focus-visible { outline: 2px solid currentColor; outline-offset: 2px; border-radius: 4px; }
`;

/* Facts on the photo: frosted chips, each a number, a label and a verdict dot. */
const Chips = styled.div`
  margin-top: auto;
  padding-top: 22px;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  max-width: 640px;

  @media (max-width: 760px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
`;

const Chip = styled(Link)`
  display: block;
  min-width: 0;
  min-height: 0;
  padding: 9px 12px;
  border-radius: 12px;
  color: inherit;
  text-decoration: none;
  background: ${p => (p.$photo ? 'rgba(255, 255, 255, 0.12)' : p.theme.colors.surfaceHover)};
  border: 1px solid ${p => (p.$photo ? 'rgba(255, 255, 255, 0.18)' : p.theme.colors.border)};
  backdrop-filter: ${p => (p.$photo ? 'blur(10px)' : 'none')};
  -webkit-backdrop-filter: ${p => (p.$photo ? 'blur(10px)' : 'none')};
  transition: background 0.15s ease;

  &:hover { background: ${p => (p.$photo ? 'rgba(255, 255, 255, 0.2)' : p.theme.colors.border)}; }
  &:focus-visible { outline: 2px solid #fff; outline-offset: 2px; }

  .v { font-size: 19px; font-weight: 500; letter-spacing: -0.02em; line-height: 1.15; white-space: nowrap; }
  .v small { font-size: 12px; font-weight: 500; opacity: 0.75; margin-left: 2px; letter-spacing: 0; }
  .k { margin-top: 2px; font-size: 12px; opacity: 0.8; display: flex; align-items: center; gap: 6px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .k i { flex: 0 0 auto; width: 6px; height: 6px; border-radius: 50%; background: ${p => statusColor(p.theme, p.$status || 'normal')}; }

  @media (prefers-reduced-transparency: reduce) { backdrop-filter: none; background: rgba(12, 22, 17, 0.7); }
`;

/* The verdict sentence (worst open problem, or "running normally") and four chips: tanks checked,
   lights on, power drawn, devices online. */
export default function Hero({ crop, items, tanks, lightSum, power, devices, manager, now }) {
  const worstOf = list => list.reduce((w, s) => (SEVERITY_RANK[s] < SEVERITY_RANK[w] ? s : w), 'notice');
  const urgent = items.filter(i => i.severity !== 'notice');
  const top = urgent[0];
  const headline = top ? `${top.subject}: ${top.text}` : 'Everything is running normally';
  const others = items.length - (top ? 1 : 0);
  const sub = top
    ? (others ? `${plural(others, 'more item')} under Needs you.` : 'Nothing else needs attention.')
    : items.length ? `Nothing urgent. ${plural(items.length, 'small item')} under Needs you.` : 'Nothing needs attention right now.';
  const dateLine = new Date((now + IST) * 1000).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });
  const cover = COVER;

  const active = tanks.filter(t => !t.maintenance);
  const flagged = active.filter(t => t.phLevel || t.ecLevel);
  const checkedToday = active.filter(t => t.checkedToday).length;
  const chips = [
    {
      k: 'tanks checked today', to: '/manual-entry',
      v: <>{checkedToday}<small>/{active.length}</small></>,
      status: flagged.length ? worstOf(flagged.flatMap(t => [t.phLevel, t.ecLevel]).filter(Boolean)) : checkedToday < active.length ? 'notice' : 'normal',
    },
    {
      k: 'lights on', to: '/hydro-units',
      v: lightSum.ids.length ? <>{lightSum.on.length}<small>/{lightSum.ids.length}</small></> : '--',
      status: lightSum.unconfirmed.length ? 'warning' : (lightSum.manual.length || lightSum.unset.length) ? 'notice' : lightSum.ids.length ? 'normal' : 'offline',
    },
    {
      k: power?.watts != null ? 'drawing now' : 'meter offline', to: '/power',
      v: power?.watts != null ? <>{Math.round(power.watts).toLocaleString('en-IN')}<small>W</small></> : '--',
      status: power?.breakerOpen ? 'critical' : power?.status === 'online' ? 'normal' : 'offline',
    },
    {
      k: 'devices online', to: '/rooms',
      v: devices.list.length ? <>{devices.list.length - devices.offline.length}<small>/{devices.list.length}</small></> : '--',
      status: !devices.list.length ? 'offline' : devices.offline.length ? 'warning' : 'normal',
    },
  ];

  return (
    <HeroShell $photo={!!cover}>
      <img src={cover.src} srcSet={cover.srcSet} sizes="(max-width: 1100px) 100vw, 66vw" alt={cover.alt} />
      <div className="cap">{cover.caption}</div>
      <div className="top">
        <div className="date">
          {dateLine}
          {crop.day != null && `, day ${crop.day}${crop.variety ? ` of the ${crop.variety} crop` : ''}`}
        </div>
        {manager?.check_in && !manager.check_out && (
          <OnSite to="/manager-log" $photo={!!cover} title="Checked in on the Manager log">
            <PiUser aria-hidden="true" /><b>Manager in</b> since {hhmm24(manager.check_in)}
          </OnSite>
        )}
      </div>
      <h1><Mark $status={top ? top.severity : 'normal'} />{headline}</h1>
      <div className="sub">{sub}</div>
      <Chips>
        {chips.map(c => (
          <Chip key={c.k} to={c.to} $photo={!!cover} $status={c.status}>
            <div className="v">{c.v}</div>
            <div className="k"><i />{c.k}</div>
          </Chip>
        ))}
      </Chips>
    </HeroShell>
  );
}
